/**
 * The Corporate Information template as rules: which rows the
 * Registration Details table has and in what order, what an empty cell
 * reads, how an officer's dates and a source line are worded, and whether
 * the section belongs on a digest at all. Pure, so the on-screen tables
 * and the tests say the same; what goes to Word and into the report is
 * the API's own rendering (`GET …/digest/markdown`). Financial
 * Information and the documents' own sections have their own rules
 * beside this file (`digestFinancials.ts`, `digestDocuments.ts`).
 *
 * The wording follows the Corporate Records template the research team
 * writes by hand: a registration value the documents do not give reads
 * "Not provided" and keeps its row; a shareholder cell the documents do
 * not give reads "not indicated".
 */

import {
  REGISTRY_DIGEST_ROLES_SCHEMA_VERSION,
  type RegistryDigestCoverage,
  type RegistryDigestDocumentCoverage,
  type RegistryDigestOfficer,
  type RegistryDigestRegistration,
  type RegistryDigestShareholding,
  type RegistryDigestSource,
  type RegistryDocumentDigest,
} from '@/types/registry-documents';

export const NOT_PROVIDED = 'Not provided';
export const NOT_INDICATED = 'not indicated';

/** What the digest names the registry when no source says. */
export const DEFAULT_REGISTRY_NAME = 'the official registry';

type RegistrationField = Exclude<keyof RegistryDigestRegistration, 'sourcePages'>;

/** The Registration Details rows, in the template's order. */
export const REGISTRATION_FIELDS: readonly { key: RegistrationField; label: string }[] = [
  { key: 'entityName', label: 'Entity Name' },
  { key: 'previousNames', label: 'Previous Registered Name(s)' },
  { key: 'aliases', label: 'Alias(es)' },
  { key: 'entityNumber', label: 'Entity Number' },
  { key: 'dunsNumber', label: 'DUNS Number' },
  { key: 'dateOfIncorporation', label: 'Date of Incorporation/Foundation' },
  { key: 'dateOfRegistration', label: 'Date of Registration' },
  { key: 'status', label: 'Status' },
  { key: 'registeredAddress', label: 'Registered Address' },
  { key: 'companyType', label: 'Company Type' },
  { key: 'businessScope', label: 'Business Scope' },
];

export const MANAGEMENT_COLUMNS = ['Name', 'Position(s)', 'Dates'] as const;
export const OWNERSHIP_COLUMNS = [
  'No.',
  'Name',
  'Entity / Passport Number',
  'Address',
  'Direct Shareholding',
] as const;

export interface LabelValueRow {
  label: string;
  value: string;
  /** False when the value is the "Not provided" placeholder. */
  provided: boolean;
}

/** A cell's text: the value as written, or the placeholder. A list is
 * joined with "; "; an empty one is not provided. */
function cellOf(value: string | string[] | null, placeholder: string): [string, boolean] {
  const text = Array.isArray(value) ? value.filter(Boolean).join('; ') : (value ?? '').trim();
  return text ? [text, true] : [placeholder, false];
}

/** The eleven Registration Details rows, every one present. */
export function registrationRows(registration: RegistryDigestRegistration): LabelValueRow[] {
  return REGISTRATION_FIELDS.map(({ key, label }) => {
    const [value, provided] = cellOf(registration[key], NOT_PROVIDED);
    return { label, value, provided };
  });
}

/** An officer's positions, as the Management table words them. */
export function officerPositions(officer: Pick<RegistryDigestOfficer, 'positions'>): string {
  return cellOf(officer.positions, NOT_INDICATED)[0];
}

/** An officer's dates: "appointed - present" for a current officer,
 * "appointed - resigned" for a former one, each end "not indicated" when
 * the documents do not give it. */
export function officerDates(
  officer: Pick<RegistryDigestOfficer, 'appointed' | 'resigned' | 'current'>
): string {
  const from = officer.appointed?.trim() || NOT_INDICATED;
  const to = officer.resigned?.trim() || (officer.current ? 'present' : NOT_INDICATED);
  return `${from} - ${to}`;
}

/** The former directors' names, alphabetical, as the paragraph lists them. */
export function formerDirectorNames(
  digest: Pick<RegistryDocumentDigest, 'formerManagement'>
): string[] {
  return digest.formerManagement
    .map((officer) => officer.name.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'en'));
}

export interface ShareholderRow {
  no: string;
  name: string;
  identifier: string;
  address: string;
  holding: string;
}

/** The Ownership / Shareholders rows, numbered, with "not indicated" in
 * every cell the documents do not give. The digest arrives ordered. */
export function shareholderRows(
  shareholders: readonly RegistryDigestShareholding[]
): ShareholderRow[] {
  return shareholders.map((holding, index) => ({
    no: String(index + 1),
    name: cellOf(holding.name, NOT_INDICATED)[0],
    identifier: cellOf(holding.identifier, NOT_INDICATED)[0],
    address: cellOf(holding.address, NOT_INDICATED)[0],
    holding: cellOf(holding.holding, NOT_INDICATED)[0],
  }));
}

/** Whether the digest has a Corporate Information section: a document was
 * read as corporate (an extract, a register, a list of officers or
 * shareholders), or the digest predates roles, when every document was. An
 * accounts-only order has none, so its digest does not open on a table of
 * "Not provided". */
export function hasCorporateInformation(
  digest: Pick<RegistryDocumentDigest, 'coverage' | 'schemaVersion'>
): boolean {
  return (
    digest.schemaVersion < REGISTRY_DIGEST_ROLES_SCHEMA_VERSION ||
    digest.coverage.documents.some(
      (document) => document.role === null || document.role === 'corporate'
    )
  );
}

/** The Management table's rows: name, positions, dates, in the digest's
 * order. */
export function managementTableRows(officers: readonly RegistryDigestOfficer[]): string[][] {
  return officers.map((officer) => [
    officer.name,
    officerPositions(officer),
    officerDates(officer),
  ]);
}

/** The Ownership / Shareholders table's rows, numbered. */
export function ownershipTableRows(
  shareholders: readonly RegistryDigestShareholding[]
): string[][] {
  return shareholderRows(shareholders).map((row) => [
    row.no,
    row.name,
    row.identifier,
    row.address,
    row.holding,
  ]);
}

/** The registry the digest names: the first source that names one. */
export function registryNameOf(digest: Pick<RegistryDocumentDigest, 'sources'>): string {
  const named = digest.sources.find((source) => source.registry?.trim());
  return named?.registry?.trim() || DEFAULT_REGISTRY_NAME;
}

/** The entity the lead-ins name: the registered name, else the order's. */
export function entityNameOf(
  digest: Pick<RegistryDocumentDigest, 'registration'>,
  companyName: string
): string {
  return digest.registration.entityName?.trim() || companyName;
}

/** The template's sentences, with the names filled in. */
export const TEMPLATE_WORDS = {
  title: 'Corporate Information',
  registrationHeading: 'Registration Details',
  registrationLeadIn: (registry: string) =>
    `Checks with the official registry (${registry}) identified the following current registration data for the company:`,
  managementHeading: 'Management',
  managementLeadIn: (entity: string) =>
    `Corporate records show that ${entity} has the following company management / directors:`,
  noManagement: (entity: string) => `No management/directors of ${entity} were identified.`,
  formerLeadIn: (registry: string, entity: string) =>
    `Further checks with the official registry (${registry}) identified the following former company management / directors for ${entity}:`,
  formerClose:
    'However, as their positions are no longer active, they were not included as principals in the report.',
  ownershipHeading: 'Ownership / Shareholders',
  ownershipLeadIn: 'Corporate records show that the company has the following ownership structure:',
  noShareholders: 'No shareholder information was identified in the documents reviewed.',
  sourcesHeading: 'APPENDIX A: SOURCES',
  sourcesLeadIn:
    'The following sources have been accessed and searched for information on the company:',
} as const;

/** The one sentence a cut document gets: how much of it was read. Null for
 * a document read in full. */
export function coverageSentence(document: RegistryDigestDocumentCoverage): string | null {
  if (!document.truncated) {
    return null;
  }
  const name = document.filename?.trim() || 'the document';
  return `Read ${document.pagesRead} of ${document.pagesTotal} pages of ${name}; the rest were not read.`;
}

/** The one sentence a run with failed model calls gets, worded as the
 * Word file words it: the tables look complete, so the reader is told
 * that part of the documents produced nothing. Null when every call
 * succeeded. */
export function failedChunksSentence(
  coverage: Pick<RegistryDigestCoverage, 'chunks' | 'chunksFailed'>
): string | null {
  if (coverage.chunksFailed <= 0) {
    return null;
  }
  return `${coverage.chunksFailed} of ${coverage.chunks} sections of the documents could not be read by the model and are not reflected above.`;
}

/** The screen-only line on officer rows the extraction left out because
 * they were read outside an officer register (a filing-history line, a
 * form's signatory). Not part of the copy for Word: it describes the run,
 * not the company. Null when none were. */
export function officersDroppedNote(
  coverage: Pick<RegistryDigestCoverage, 'officersDropped'>
): string | null {
  const count = coverage.officersDropped;
  if (count <= 0) {
    return null;
  }
  return count === 1
    ? '1 officer named outside the officer register was left out'
    : `${count} officers named outside the officer register were left out`;
}

/** Everything the tables leave unsaid about what was read: one sentence
 * per cut document, then one for the model calls that failed. Shared by
 * the screen, the copy for Word and the tests, so none of them says less
 * than the Word file. */
export function coverageNotes(digest: Pick<RegistryDocumentDigest, 'coverage'>): string[] {
  const notes = digest.coverage.documents
    .map(coverageSentence)
    .filter((sentence): sentence is string => sentence !== null);
  const failed = failedChunksSentence(digest.coverage);
  if (failed !== null) {
    notes.push(failed);
  }
  return notes;
}

/** Page numbers as a short list: "pages 1-3, 7". Null for none. */
export function describePages(pages: readonly number[]): string | null {
  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  if (sorted.length === 0) {
    return null;
  }
  const runs: string[] = [];
  let start = sorted[0];
  let end = start;
  for (const page of sorted.slice(1)) {
    if (page === end + 1) {
      end = page;
    } else {
      runs.push(start === end ? String(start) : `${start}-${end}`);
      start = page;
      end = page;
    }
  }
  runs.push(start === end ? String(start) : `${start}-${end}`);
  return `${sorted.length === 1 ? 'page' : 'pages'} ${runs.join(', ')}`;
}

/** A date in the report's written form, "1 October 2026": the form the
 * digest stores every date in, so a fallback the Hub supplies (the delivery
 * day) reads like the dates the document gave. The UTC day, never the
 * viewer's zone. Null for anything that is not a date. */
export function writtenDate(value: string | Date | null | undefined): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** One line of the sources appendix: registry, document title, pages,
 * retrieved date. `retrieved` falls back to when the order was delivered,
 * which the caller knows and the document does not. */
export function sourceLine(source: RegistryDigestSource, retrievedFallback: string | null): string {
  const parts = [
    source.registry?.trim() || DEFAULT_REGISTRY_NAME,
    source.documentTitle?.trim() || null,
    describePages(source.sourcePages),
  ].filter((part): part is string => part !== null);
  const retrieved = source.retrieved?.trim() || retrievedFallback?.trim() || null;
  if (retrieved) {
    parts.push(`retrieved ${retrieved}`);
  }
  return parts.join(', ');
}
